import React, { useEffect, useRef, useState } from "react";
import {
  getNotificationApi,
  getNotificationCountApi,
  markReadNotificationApi,
} from "./Notification.api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "context/auth-context";
import PointUIModel from "./PointUIModel";
import SurveyUIModel from "./SurveyUIModel";
import { useTranslation } from "react-i18next";

export const ShowNotificationModel = ({
  showNotificationModel,
  setShowNotificationModel,
  setNotificationCount,
  notificationCount,
}: any) => {
  const [activeTab, setActiveTab] = React.useState("Rewards");
  const [pointNotiCount, setPointNotiCount] = useState([{ count: 0 }]);
  const [surveyNotiCount, setSurveyNotiCount] = useState([{ count: 0 }]);
  const [tabChangeCount, setTabChangeCount] = useState(0);
  const { t } = useTranslation();
  const { auth } = useAuth();
  const queryClient = useQueryClient();
  const modalRef = useRef<HTMLDivElement>(null);
  const type = activeTab === "Rewards" ? "point" : "survey";

  //notification count
  const getNotificationCount = useQuery(
    ["notificationCount", auth.panelistId, activeTab],
    () => getNotificationCountApi(auth.panelistId),
    {
      refetchOnWindowFocus: false,
    }
  );

  // Use `refetch` whenever the modal state changes
  useEffect(() => {
    if (getNotificationCount?.data) {
      setNotificationCount(getNotificationCount?.data?.unreadCount);
      setPointNotiCount(getNotificationCount.data.pointCount || [{ count: 0 }]);
      setSurveyNotiCount(getNotificationCount.data.surveyCount || [{ count: 0 }]);
    }
  }, [getNotificationCount?.data, activeTab, tabChangeCount, notificationCount]);

  // Fetch notifications with proper dependency on activeTab
  const { data } = useQuery(
    ["notification", auth.panelistId, type],
    () => getNotificationApi(auth.panelistId, type),
    {
      enabled: showNotificationModel,
      refetchOnWindowFocus: false,
    }
  );

  // Mark notifications as read
  const markAsReadMutation = useMutation(
    (notificationIds: number[]) => markReadNotificationApi(auth.panelistId, notificationIds, type),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["notification", auth.panelistId, type]);
      },
    }
  );

  const handleModalClose = () => {
    setShowNotificationModel(false);
    if (data?.NotificationData) {
      // Automatically mark unread notifications as read
      const unreadNotifications = data.NotificationData.filter(
        (notification: any) => !notification.mark_as_read
      );
      if (unreadNotifications.length > 0) {
        const unreadIds = unreadNotifications.map((notification: any) => notification.id);
        markAsReadMutation.mutate(unreadIds);
      }
    }
  };

  // Handle tab change
  const handleTabChange = (tab: string) => {
    if (tab !== activeTab) {
      setActiveTab(tab);
      setPointNotiCount(getNotificationCount.data.pointCount || [{ count: 0 }]);
      setSurveyNotiCount(getNotificationCount.data.surveyCount || [{ count: 0 }]);
      // Increment tab change count
      setTabChangeCount((prevCount) => prevCount + 1);
    }
    queryClient.invalidateQueries(["notificationCount", auth.panelistId, tab]);
    queryClient.invalidateQueries(["notification", auth.panelistId, tab]);
  };

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        handleModalClose();
      }
    };

    if (showNotificationModel) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showNotificationModel, data]);

  return (
    <div
      className="w-full lg:w-[32%] 2xl:w-[25%] h-[80vh] sm:h-[90vh] lg:h-[80vh] overflow-y-auto bg-white rounded-3xl fixed left-0 top-20 lg:right-10 lg:left-auto lg:top-[85px] p-4 z-50 shadow-2xl font-poppins"
      style={{ WebkitOverflowScrolling: "touch", scrollbarWidth: "none", msOverflowStyle: "none" }}
      ref={modalRef}
    >
      <h2 className="mb-4 text-xl font-semibold">{t("point.notification.available2")}</h2>
      {/* Close Modal */}
      <div className="mt-4 text-center">
        <button onClick={handleModalClose} className="absolute px-4 py-2 text-xl top-4 right-4">
          X
        </button>
      </div>
      {/* Tab Navigation */}
      <div className="flex justify-around mb-4 font-poppins">
        {/* Rewards Tab */}
        <div className="relative">
          <button
            onClick={() => handleTabChange("Rewards")}
            className={`px-6 py-1.5 text-lg ${
              activeTab === "Rewards" ? "border-2 border-[#056E9C2E] rounded-xl" : ""
            }`}
          >
            {t("LovedAboutUs.rewards.title.1")}
          </button>
          {pointNotiCount[0]?.count > 0 && activeTab !== "Rewards" && (
            <span className="absolute top-2 right-3 bg-red-500 w-2 h-2 rounded-full animate-ping"></span>
          )}
        </div>

        {/* Survey Tab */}
        <div className="relative">
          <button
            onClick={() => handleTabChange("Survey")}
            className={`px-6 py-1.5 text-lg ${
              activeTab === "Survey" ? "border-2 border-[#056E9C2E] rounded-xl" : ""
            }`}
          >
            {t("point.notification.Survey")}
          </button>
          {surveyNotiCount[0]?.count > 0 && activeTab !== "Survey" && (
            <span className="absolute top-2 right-3 bg-red-500 w-2 h-2 rounded-full animate-ping"></span>
          )}
        </div>

        {/* General Tab */}
        <div className="relative">
          <button
            onClick={() => handleTabChange("General")}
            className={`px-6 py-1.5 text-lg ${
              activeTab === "General" ? "border-2 border-[#056E9C2E] rounded-xl" : ""
            }`}
          >
            {t("point.notification.General")}
          </button>
        </div>
      </div>
      {activeTab === "Rewards" && (
        <PointUIModel
          showNotificationModel={showNotificationModel}
          setNotificationCount={setNotificationCount}
          activeTab={activeTab}
        />
      )}
      {activeTab === "Survey" && (
        <SurveyUIModel
          showNotificationModel={showNotificationModel}
          setNotificationCount={setNotificationCount}
          activeTab={activeTab}
        />
      )}

      {/* Notification List */}
    </div>
  );
};
